import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { IMAGES, JAR_SIZES } from "@/lib/constants";
import {
  ArrowLeft, Trash2, ShoppingBag, Loader2, CreditCard, Check, Package,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]')) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function CartPage() {
  const navigate = useNavigate();
  const { user, authHeaders, setCartCount } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(null);

  useEffect(() => {
    document.title = "Cart | Swadishes";
    let isMounted = true;
    
    const loadCart = async () => {
      setLoading(true);
      const headers = authHeaders();
      try {
        const res = await fetch(`${API}/api/cart`, { headers });
        if (res.ok && isMounted) {
          const data = await res.json();
          setItems(data);
          setCartCount(data.length);
        }
      } catch (err) {
        console.error("Error loading cart:", err);
      }
      if (isMounted) setLoading(false);
    };
    
    loadCart();
    return () => { 
      isMounted = false;
      document.title = "Swadishes"; 
    };
  }, [authHeaders, setCartCount]);

  const removeItem = async (id) => {
    const headers = authHeaders();
    const res = await fetch(`${API}/api/cart/${id}`, { method: "DELETE", headers });
    if (res.ok) {
      const data = await res.json();
      setItems((prev) => prev.filter((i) => i.id !== id));
      setCartCount(data.cart_count);
    }
  };

  const clearCart = async () => {
    const headers = authHeaders();
    await fetch(`${API}/api/cart`, { method: "DELETE", headers });
    setItems([]);
    setCartCount(0);
  };

  const total = items.reduce((sum, item) => sum + (item.price || 349), 0);

  const handleCheckout = async () => {
    setPaying(true);
    const headers = authHeaders();
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) { alert("Failed to load payment gateway"); setPaying(false); return; }

      const res = await fetch(`${API}/api/checkout/create-order`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (!res.ok) { alert("Could not create order"); setPaying(false); return; }
      const orderData = await res.json();

      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        order_id: orderData.order_id,
        name: "Swadishes",
        description: `${items.length} custom pickle${items.length > 1 ? "s" : ""}`,
        handler: async (response) => {
          const verifyRes = await fetch(`${API}/api/checkout/verify-payment`, {
            method: "POST",
            headers: { ...headers, "Content-Type": "application/json" },
            body: JSON.stringify({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            }),
          });
          if (verifyRes.ok) {
            const data = await verifyRes.json();
            setOrderSuccess(data.order);
            setItems([]);
            setCartCount(0);
          } else {
            alert("Payment verification failed. Please contact support.");
          }
          setPaying(false);
        },
        modal: {
          ondismiss: () => setPaying(false),
        },
        prefill: {
          name: user?.name || "",
          email: user?.email || "",
        },
        theme: { color: "#4A5D23" },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      alert("Checkout failed: " + err.message);
      setPaying(false);
    }
  };

  if (orderSuccess) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-4" data-testid="order-success">
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-3xl p-8 max-w-md w-full text-center shadow-xl">
          <div className="w-16 h-16 rounded-full bg-earth-green/10 flex items-center justify-center mx-auto mb-4">
            <Check size={32} className="text-earth-green" />
          </div>
          <h2 className="text-xl font-heading font-700 text-sw-text mb-2">Order Confirmed!</h2>
          <p className="text-sm text-sw-text-secondary mb-1">Order #{orderSuccess.id?.slice(0, 8)}</p>
          <p className="text-2xl font-heading font-800 text-earth-green mb-4">&#8377;{orderSuccess.total}</p>
          <p className="text-sm text-sw-text-muted mb-6">Your custom pickles are being prepared with love.</p>
          <div className="space-y-2 mb-6 text-left">
            {orderSuccess.items?.map((item, i) => (
              <div key={i} className="flex justify-between text-sm bg-cream-secondary rounded-xl px-4 py-2.5">
                <span className="text-sw-text-secondary">{item.pickle_name || "Custom Pickle"}</span>
                <span className="font-medium text-sw-text">&#8377;{item.price}</span>
              </div>
            ))}
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <button onClick={() => navigate("/my-kitchen")} data-testid="order-view-kitchen"
              className="flex-1 border border-earth-green text-earth-green rounded-full py-3 text-sm font-medium hover:bg-earth-green hover:text-white transition-all">
              My Kitchen
            </button>
            <button onClick={() => navigate("/studio")} data-testid="order-new-recipe"
              className="flex-1 bg-earth-green text-white rounded-full py-3 text-sm font-medium hover:bg-earth-green-dark transition-all">
              New Recipe
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-earth-green" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream" data-testid="cart-page">
      <header className="fixed top-0 w-full z-50 bg-cream/90 backdrop-blur-xl border-b border-gray-100/50">
        <div className="section-container flex items-center justify-between h-14 lg:h-16">
          <button onClick={() => navigate(-1)} data-testid="cart-back-btn" className="flex items-center gap-2 text-sw-text-secondary hover:text-earth-green transition-colors">
            <ArrowLeft size={18} />
            <span className="text-sm font-medium hidden sm:block">Back</span>
          </button>
          <div className="flex items-center gap-2">
            <img src={IMAGES.logo} alt="Swadishes" className="h-7 lg:h-9 rounded-md" />
            <span className="text-sm lg:text-base font-heading font-700 text-sw-text">Cart</span>
          </div>
          <div className="w-16" />
        </div>
      </header>

      <main className="pt-20 lg:pt-24 pb-32 lg:pb-16">
        <div className="section-container max-w-2xl mx-auto">
          {items.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-3xl border border-gray-100" data-testid="empty-cart">
              <ShoppingBag size={40} className="text-sw-text-muted/30 mx-auto mb-4" />
              <p className="text-sw-text-muted mb-4">Your cart is empty</p>
              <button onClick={() => navigate("/studio")} className="bg-earth-green text-white rounded-full px-6 py-2.5 text-sm font-medium">
                Start Creating
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <h1 className="text-xl font-heading font-700 text-sw-text">{items.length} item{items.length > 1 ? "s" : ""}</h1>
                <button onClick={clearCart} data-testid="cart-clear-btn" className="text-xs text-sw-text-muted hover:text-red-500 transition-colors">
                  Clear All
                </button>
              </div>

              <div className="space-y-3 mb-6" data-testid="cart-items">
                <AnimatePresence>
                  {items.map((item) => {
                    const jar = JAR_SIZES.find((j) => j.id === item.jar_size);
                    return (
                      <motion.div key={item.id} layout exit={{ opacity: 0, x: -50 }}
                        className="flex flex-col gap-3 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between"
                        data-testid={`cart-item-${item.id}`}>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-heading font-700 text-sw-text text-sm truncate">{item.pickle_name || "Custom Pickle"}</h3>
                          <p className="text-xs text-sw-text-muted mt-0.5">{jar?.name || "Regular"} &middot; {jar?.weight || "300g"}</p>
                        </div>
                        <div className="flex items-center justify-between gap-4 sm:ml-4 sm:justify-end">
                          <span className="text-base font-heading font-700 text-earth-green">&#8377;{item.price}</span>
                          <button onClick={() => removeItem(item.id)} data-testid={`cart-remove-${item.id}`}
                            className="p-2 rounded-full text-sw-text-muted hover:text-red-500 hover:bg-red-50 transition-all">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>

              {/* Summary */}
              <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm" data-testid="cart-summary">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-sw-text-secondary">Subtotal</span>
                  <span className="font-medium text-sw-text">&#8377;{total}</span>
                </div>
                <div className="flex justify-between text-sm mb-3">
                  <span className="text-sw-text-secondary">Delivery</span>
                  <span className="text-earth-green font-medium">FREE</span>
                </div>
                <div className="flex justify-between pt-3 border-t border-gray-100">
                  <span className="text-base font-heading font-700 text-sw-text">Total</span>
                  <span className="text-xl font-heading font-800 text-earth-green">&#8377;{total}</span>
                </div>
              </div>

              {/* Checkout Button */}
              <div className="mt-6">
                <button onClick={handleCheckout} disabled={paying} data-testid="checkout-btn"
                  className="w-full bg-earth-green text-white rounded-full py-4 font-medium text-base hover:bg-earth-green-dark transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-earth-green/20">
                  {paying ? <Loader2 size={18} className="animate-spin" /> : <CreditCard size={18} />}
                  {paying ? "Processing..." : <>Pay ₹{total}</>}
                </button>
                <p className="text-xs text-sw-text-muted text-center mt-3">Secured by Razorpay. We never store your card details.</p>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
